import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import {
  startListening,
  type AccessShareListenerHandle,
} from "@/lib/accessShareClient";
import {
  encryptAccessKey,
  exportPublicKey,
  generateEcdhKeyPair,
  importPublicKey,
} from "@/lib/accessShareCrypto";
import type { PendingAccessRequest } from "@/lib/accessShareProtocol";

type SettingsTab = "access" | "key";

const SETTINGS_TABS: { id: SettingsTab; label: string }[] = [
  { id: "access", label: "Access" },
  { id: "key", label: "Key" },
];

export function SettingsRoute() {
  const { setBreadcrumbSegments } = useBreadcrumbs();
  const [activeTab, setActiveTab] = useState<SettingsTab>("access");
  const [key, setKey] = useState(() =>
    typeof window !== "undefined" ? String(window.localStorage.accessKey ?? "") : "",
  );
  const [isListening, setIsListening] = useState(false);
  const [requests, setRequests] = useState<PendingAccessRequest[]>([]);
  const [listenError, setListenError] = useState<string | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);
  const listenerRef = useRef<AccessShareListenerHandle | null>(null);
  const keyPairRef = useRef<CryptoKeyPair | null>(null);
  const startingRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    setBreadcrumbSegments([{ label: "Settings" }]);
    return () => setBreadcrumbSegments([]);
  }, [setBreadcrumbSegments]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      listenerRef.current?.stop();
      listenerRef.current = null;
      keyPairRef.current = null;
    };
  }, []);

  const handleStartListening = useCallback(async () => {
    if (startingRef.current || listenerRef.current) return;
    startingRef.current = true;
    setListenError(null);
    try {
      const keyPair = await generateEcdhKeyPair();
      if (!aliveRef.current) return;
      keyPairRef.current = keyPair;
      const handle = startListening({
        onRequestsChange: ({ requests: next }) => setRequests(next),
        onError: ({ message }) => setListenError(message),
        onDisconnected: () => {
          setIsListening(false);
          setRequests([]);
          listenerRef.current = null;
          keyPairRef.current = null;
        },
      });
      if (!aliveRef.current) {
        handle.stop();
        return;
      }
      listenerRef.current = handle;
      setIsListening(true);
    } catch {
      keyPairRef.current = null;
      setListenError("Could not start listening.");
    } finally {
      startingRef.current = false;
    }
  }, []);

  const handleStopListening = useCallback(() => {
    listenerRef.current?.stop();
    listenerRef.current = null;
    keyPairRef.current = null;
    setIsListening(false);
    setRequests([]);
    setListenError(null);
  }, []);

  const handleApprove = useCallback(async ({ request }: { request: PendingAccessRequest }) => {
    const keyPair = keyPairRef.current;
    const handle = listenerRef.current;
    const accessKey =
      typeof window !== "undefined" ? String(window.localStorage.accessKey ?? "") : "";
    if (!keyPair || !handle) return;
    if (!accessKey.trim()) {
      setListenError("No access key is stored.");
      return;
    }
    setBusyRequestId(request.id);
    setListenError(null);
    try {
      const peerPublicKey = await importPublicKey({ publicKey: request.publicKey });
      const { encrypted, iv } = await encryptAccessKey({
        plaintext: accessKey,
        privateKey: keyPair.privateKey,
        peerPublicKey,
      });
      const publicKeyA = await exportPublicKey({ key: keyPair.publicKey });
      handle.approve({
        requestId: request.id,
        publicKeyA,
        encrypted,
        iv,
      });
    } catch {
      setListenError("Could not send the key.");
    } finally {
      setBusyRequestId(null);
    }
  }, []);

  const handleDeny = useCallback(({ requestId }: { requestId: string }) => {
    listenerRef.current?.deny({ requestId });
  }, []);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6 p-8">
      <h1 className="text-base font-semibold leading-none tracking-tight">Settings</h1>

      <div className="space-y-4">
        <div
          role="tablist"
          aria-label="Settings sections"
          className="border-border flex flex-wrap gap-2 border-b pb-3"
        >
          {SETTINGS_TABS.map((tab) => (
            <Button
              key={tab.id}
              type="button"
              size="sm"
              role="tab"
              aria-selected={activeTab === tab.id}
              variant={activeTab === tab.id ? "default" : "secondary"}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {activeTab === "access" && (
          <Card>
            <CardContent className="space-y-4 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-muted-foreground text-sm">
                  {isListening
                    ? "Listening for requests…"
                    : "Press the button to receive access requests."}
                </p>
                {isListening ? (
                  <Button type="button" variant="outline" onClick={handleStopListening}>
                    Stop listening
                  </Button>
                ) : (
                  <Button type="button" onClick={() => void handleStartListening()}>
                    Start listening
                  </Button>
                )}
              </div>

              {listenError && (
                <p className="text-destructive text-sm" role="alert">
                  {listenError}
                </p>
              )}

              {isListening && requests.length === 0 ? (
                <p className="text-muted-foreground text-sm">No pending requests.</p>
              ) : null}

              {requests.length > 0 && (
                <ul className="space-y-3">
                  {requests.map((request) => (
                    <li
                      key={request.id}
                      className="border-border flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                    >
                      <p className="font-mono text-2xl tracking-[0.3em]">{request.pairingCode}</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={busyRequestId === request.id}
                          onClick={() => void handleApprove({ request })}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busyRequestId === request.id}
                          onClick={() => handleDeny({ requestId: request.id })}
                        >
                          Deny
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "key" && (
          <Card>
            <CardContent className="p-4">
              <form
                className="flex max-w-sm flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  localStorage.accessKey = key;
                  window.location.reload();
                }}
              >
                <div className="grid gap-2">
                  <Label htmlFor="settings-access-key">Key</Label>
                  <Input
                    id="settings-access-key"
                    name="accessKey"
                    type="password"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit">Save</Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
